import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Sparkle, ArrowRight, Star, Download, ShareNetwork } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Compact CV rating history card on the candidate dashboard.
 * States:
 *   - never rated → CTA to /rate-my-cv
 *   - has ratings → shows latest score + score band + link to view all
 *   - if there's a rewrite ready → shows Download button
 *   - Share button generates OG-image slug and copies /share/cv/<slug> to clipboard
 */
const BAND = {
    green: { fg: "text-[#059669]", ring: "ring-[#059669]", bg: "bg-[#059669]/10", label: "Strong" },
    amber: { fg: "text-[#F59E0B]", ring: "ring-[#F59E0B]", bg: "bg-[#F59E0B]/10", label: "Improve" },
    red:   { fg: "text-[#DC2626]", ring: "ring-[#DC2626]", bg: "bg-[#DC2626]/10", label: "Rewrite" },
};

export default function CandidateCVCard() {
    const [state, setState] = useState(null); // {items, free_rewrites_remaining, ...}
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let alive = true;
        api.get("/me/cv/ratings")
            .then((r) => { if (alive) setState(r.data); })
            .catch(() => { /* no-op */ });
        return () => { alive = false; };
    }, []);

    if (!state) return null;

    const items = state.items || [];
    const latest = items[0];
    // Find the most-recent rating that actually has a rewrite (may not be `latest`).
    const ratingWithRewrite = items.find((r) => r.rewritten_pdf_id);
    const totalRatings = state.count || items.length;
    const hasRewrite = !!ratingWithRewrite;
    const band = BAND[latest?.band] || BAND.amber;

    const downloadPdf = () => {
        const target = ratingWithRewrite || latest;
        if (!target) return;
        const url = `${API}/cv/ratings/${target.id}/download`;
        // Sandboxed iframes (e.g., the Emergent preview) block in-frame
        // downloads silently. Detect and fall back to a popup only in that
        // case. Real users on the live domain get a direct save with no tab.
        const inIframe = window.self !== window.top;
        if (inIframe) {
            window.open(url, "_blank", "noopener");
        } else {
            const a = document.createElement("a");
            a.href = url;
            a.download = `NaukriDaddy_CV_${target.id.slice(0, 8)}.pdf`;
            document.body.appendChild(a);
            a.click();
            setTimeout(() => document.body.removeChild(a), 200);
        }
        toast.success("Download started.");
    };

    const openShare = async () => {
        if (busy || !latest) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/cv/ratings/${latest.id}/share`);
            const url = `${window.location.origin}/api/share/cv/${data.slug}`;
            const scoreLabel = `${latest.overall_score}/10`;
            const shareText = `I got ${scoreLabel} on the NaukriDaddy AI CV rating. Rate yours free in 30 sec 👉`;
            // Prefer the native Web Share API (WhatsApp / LinkedIn / any installed app).
            if (navigator.share) {
                try {
                    await navigator.share({
                        title: `My CV scored ${scoreLabel}`,
                        text: shareText,
                        url,
                    });
                    toast.success("Shared!");
                    return;
                } catch (err) {
                    // User cancelled — treat silently. Fall through to clipboard.
                    if (err?.name === "AbortError") return;
                }
            }
            // Fallback 1: clipboard copy
            let copied = false;
            try {
                await navigator.clipboard.writeText(`${shareText} ${url}`);
                copied = true;
            } catch (_) {
                try {
                    const ta = document.createElement("textarea");
                    ta.value = `${shareText} ${url}`;
                    ta.style.position = "fixed";
                    ta.style.opacity = "0";
                    document.body.appendChild(ta);
                    ta.select();
                    copied = document.execCommand("copy");
                    document.body.removeChild(ta);
                } catch (_) { /* ignore */ }
            }
            if (copied) {
                toast.success("Share link copied — paste on LinkedIn or WhatsApp!");
            } else {
                toast(`Copy this: ${url}`, { duration: 15000 });
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't create share link.");
        } finally {
            setBusy(false);
        }
    };

    // Zero-state — big friendly CTA
    if (!latest) {
        return (
            <section
                data-testid="candidate-cv-card"
                className="border border-cobalt bg-gradient-to-br from-cobalt/5 via-white to-splash/5 p-5 mb-6"
            >
                <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-cobalt text-white flex items-center justify-center shrink-0">
                        <Sparkle size={18} weight="fill" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-cobalt mb-1">
                            Free AI-powered
                        </p>
                        <p className="font-heading text-lg sm:text-xl font-bold tracking-tight text-ink mb-1">
                            Rate your CV in 30 seconds.
                        </p>
                        <p className="text-xs text-[#525252] mb-3">
                            Get a score out of 10 and see the gaps costing you interviews.
                        </p>
                        <Link
                            to="/rate-my-cv"
                            data-testid="candidate-cv-empty-cta"
                            className="btn-gradient inline-flex items-center gap-2 px-4 py-2.5 text-[11px] font-black uppercase tracking-wider"
                        >
                            <Sparkle size={11} weight="fill" /> Rate my CV <ArrowRight size={11} weight="bold" />
                        </Link>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section
            data-testid="candidate-cv-card"
            className="border border-[#E5E5E5] bg-white p-5 mb-6"
        >
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                {/* Score ring */}
                <div className={`w-20 h-20 rounded-full border-4 ${band.ring} ${band.bg} flex flex-col items-center justify-center shrink-0`} data-testid="candidate-cv-score">
                    <span className={`font-heading text-2xl font-black tracking-tighter ${band.fg} tabular-nums`}>
                        {latest.overall_score}
                    </span>
                    <span className="text-[8px] font-black uppercase tracking-wider text-[#525252]">/ 10</span>
                </div>

                {/* Text */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#525252]">
                            Your CV rating
                        </p>
                        <span className={`text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 ${band.bg} ${band.fg}`}>
                            {band.label}
                        </span>
                    </div>
                    <p className="text-sm text-ink font-bold mb-1" data-testid="candidate-cv-summary">
                        {latest.summary || "Your latest CV rating"}
                    </p>
                    <p className="text-[11px] text-[#A3A3A3]">
                        {totalRatings} rating{totalRatings === 1 ? "" : "s"}
                        {(state.free_rewrites_remaining ?? 0) > 0 && " · Free rewrite available"}
                    </p>
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:items-end gap-2 shrink-0">
                    {hasRewrite && (
                        <button
                            onClick={downloadPdf}
                            data-testid="candidate-cv-download"
                            className="border border-[#059669] text-[#059669] hover:bg-[#059669] hover:text-white px-3 py-1.5 text-[10px] font-black uppercase tracking-wider inline-flex items-center gap-1 transition-colors"
                        >
                            <Download size={11} weight="bold" /> Download PDF
                        </button>
                    )}
                    <div className="flex items-center gap-2">
                        <button
                            onClick={openShare}
                            disabled={busy}
                            data-testid="candidate-cv-share"
                            title="Share your score"
                            className="border border-[#E5E5E5] hover:border-ink text-[#525252] hover:text-ink px-2.5 py-1.5 text-[10px] font-black uppercase tracking-wider inline-flex items-center gap-1 transition-colors disabled:opacity-50"
                        >
                            <ShareNetwork size={11} weight="bold" /> Share
                        </button>
                        <Link
                            to="/rate-my-cv"
                            data-testid="candidate-cv-open"
                            className="border border-cobalt text-cobalt hover:bg-cobalt hover:text-white px-3 py-1.5 text-[10px] font-black uppercase tracking-wider inline-flex items-center gap-1 transition-colors"
                        >
                            <Star size={11} weight="fill" /> {(state.free_rewrites_remaining ?? 0) > 0 ? "Fix gaps free" : "Rate again"}
                            <ArrowRight size={11} weight="bold" />
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
}
